import React from 'react';
import { 
  Paper, 
  BottomNavigation, 
  BottomNavigationAction,
  Box
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { isAppRoute, getAppRelativePath } from '../config/routes';

// Import icons
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';

// Import styling from our design system
import { colors, shadows } from '../styles/styleConstants';

/**
 * Bottom navigation component for the mobile web GUI
 * Provides navigation to key sections of the app
 */
const BottomNav = () => {
  const navigate = useAppNavigation();
  const location = useLocation();
  
  // Determine current active route
  const getActiveRoute = () => {
    const path = location.pathname;
    
    // Only show navigation for app routes
    if (!isAppRoute(path)) return -1;
    
    // Get the app-relative path
    const relativePath = getAppRelativePath(path);
    
    if (relativePath === '/' || relativePath === '') return 0;
    if (relativePath.startsWith('/find') || relativePath.startsWith('/all-items') || relativePath.startsWith('/all-boxes') || relativePath.startsWith('/box/')) return 1;
    if (relativePath.startsWith('/add')) return 2;
    if (relativePath.startsWith('/settings') || relativePath.startsWith('/let-go') || relativePath.startsWith('/exit-bin')) return 3;
    if (relativePath.startsWith('/profile')) return 4;
    
    // Show nav for other app routes but don't highlight any tab
    return 0;
  };
  
  const activeRoute = getActiveRoute();

  // Only render the navigation for app routes
  if (activeRoute === -1) return null;

  return (
    <Paper 
      sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0,
        zIndex: 1000,
        borderTop: `1px solid ${colors.divider}`,
        borderRadius: '24px 24px 0 0',
        overflow: 'hidden',
        boxShadow: shadows.lg,
      }} 
      elevation={0}
    >
      <BottomNavigation
        showLabels
        value={activeRoute}
        sx={{
          bgcolor: colors.softWhite,
          height: 68,
          '& .MuiBottomNavigationAction-root': {
            color: colors.mediumSlateBlue,
            paddingTop: '10px',
            minWidth: 0,
            '&.Mui-selected': {
              color: colors.actionBlue,
            }
          },
        }}
        onChange={(event, newValue) => {
          switch (newValue) {
            case 0:
              navigate.to.HOME();
              break;
            case 1:
              navigate.to.FIND();
              break;
            case 2:
              navigate.to.ADD();
              break;
            case 3:
              navigate.to.SETTINGS();
              break;
            case 4:
              navigate.to.PROFILE();
              break;
            default:
              navigate.to.HOME();
          }
        }}
      >
        <BottomNavigationAction 
          label="Home" 
          icon={<HomeRoundedIcon />} 
        />

        <BottomNavigationAction 
          label="Find" 
          icon={<SearchRoundedIcon />} 
        />
        
        <BottomNavigationAction 
          label="Add" 
          icon={
            <Box sx={{ 
              bgcolor: activeRoute === 2 ? colors.actionBlue : 'rgba(59, 130, 246, 0.1)',
              color: activeRoute === 2 ? 'white' : colors.actionBlue,
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'translateY(-10px)',
              boxShadow: activeRoute === 2 ? shadows.md : 'none',
              transition: 'all 0.2s'
            }}>
              <AddCircleRoundedIcon sx={{ fontSize: 28 }} />
            </Box>
          } 
        />
        
        <BottomNavigationAction 
          label="Settings" 
          icon={<SettingsRoundedIcon />} 
        />
        
        <BottomNavigationAction 
          label="Profile" 
          icon={<PersonRoundedIcon />} 
        />
      </BottomNavigation>
    </Paper>
  );
};

export default BottomNav;
